import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { io, Socket } from 'socket.io-client';
import { useAuthContext } from '../common/hooks/use-auth-context.hook';
import { useNotificationsApi } from '../common/hooks/use-notifications-api.hook';
import { useNotificationStore } from '../common/stores/notification.store';
import AuthClientStore from '../common/services/auth-client.service';
import { attachRealtimeSocket, detachRealtimeSocket } from '../common/services/realtime.service';
import { endSessionLocally } from '../common/services/device-session.service';

const API_URL = process.env.REACT_APP_PMS_API_URL || 'http://localhost:8082';

// Polling is kept as a fallback / drift correction while the socket is the
// primary channel — a missed event (reconnect gap, server restart) is healed
// on the next poll.
const POLL_INTERVAL_MS = 60_000;

/** Categories worth a toast the moment they arrive; the rest wait in the bell. */
const LIVE_CATEGORIES = new Set(['ardi', 'leads', 'support', 'sales']);

/**
 * Drives the topbar bell: an authenticated socket on the API's /notifications
 * namespace pushes new notifications in real time, backed by an unread-count
 * poll. Mounted once in App.tsx (inside AuthProvider) because TopNavBar
 * remounts on every navigation via PageContainer. Resets the store on logout.
 */
export const NotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthContext();
  const api = useNotificationsApi();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthenticated) {
      useNotificationStore.getState().reset();
      return;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const { count } = await api.getUnreadCount();
        if (!cancelled) useNotificationStore.getState().setUnreadCount(count);
      } catch {
        // Silent — the bell just keeps its last known count.
      }
    };
    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    // Default transports (polling → websocket upgrade) on purpose: listing
    // websocket first disables socket.io's fallback, so a CSP/proxy that blocks
    // ws would kill real-time entirely instead of degrading to polling.
    const socket: Socket = io(`${API_URL}/notifications`, {
      reconnection: true,
      reconnectionDelay: 2000,
      auth: { token: AuthClientStore.getAccessToken() },
    });

    // Access tokens rotate (1h expiry) — re-read localStorage before every
    // reconnect attempt so a long-lived tab reconnects with a fresh token.
    socket.io.on('reconnect_attempt', () => {
      (socket.auth as { token?: string }).token = AuthClientStore.getAccessToken();
    });

    /*
     * A push says "something arrived", not what to show. The room is the
     * whole vendor, and the payload is the raw row — it knows neither this
     * person's language nor the types they turned off. So the count and the
     * first page are re-read, which the API renders and filters for them.
     * Debounced: a burst (an import finishing, a routine) is one re-read.
     */
    let refetch: ReturnType<typeof setTimeout> | null = null;
    const arrived = new Set<string>();
    socket.on('notification:new', (notification) => {
      if (cancelled || !notification?._id) return;
      useNotificationStore.getState().noteArrival(notification.type);
      arrived.add(String(notification._id));
      if (refetch) clearTimeout(refetch);
      refetch = setTimeout(async () => {
        try {
          const [{ count }, page] = await Promise.all([api.getUnreadCount(), api.findAll({ page: 1, limit: 20 })]);
          if (cancelled) return;
          useNotificationStore.getState().setUnreadCount(count);
          useNotificationStore.getState().setItems(page.data);
          /*
           * What Ardi noticed and who just became a lead are said out loud,
           * once, in a quiet toast — the moment is the point. Only rows that
           * came back in this person's list: a type they turned off never
           * reaches them here either. Two at most, however many arrived.
           */
          const fresh = page.data.filter((n) => arrived.has(n._id) && LIVE_CATEGORIES.has(n.category ?? '') && !n.read).slice(0, 2);
          arrived.clear();
          for (const n of fresh) {
            toast(n.title, {
              description: n.body || undefined,
              action: n.route ? { label: t('ARDI.NOTIFICATIONS.OPEN') as string, onClick: () => navigate(n.route as string) } : undefined,
            });
          }
        } catch {
          // The next poll heals it.
        }
      }, 400);
    });

    // Signed out from elsewhere (another sign-in, the Devices page, support):
    // leave now instead of at the next request.
    socket.on('session:revoked', (payload) => {
      if (!cancelled) endSessionLocally(payload?.reason || 'revoked_by_vendor');
    });

    // Other features (support chat) listen on this same connection.
    attachRealtimeSocket(socket);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      if (refetch) clearTimeout(refetch);
      detachRealtimeSocket(socket);
      socket.disconnect();
    };
    // `api` is re-created on every render; isAuthenticated is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return <>{children}</>;
};
